import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listInstances } from '@/api/real-browsers';
import type { InstanceStatus } from '@/types';
import InstanceCard from '@/components/InstanceCard';
import { cn } from '@/lib/cn';

const STATUSES: Array<InstanceStatus | 'all'> = ['all', 'healthy', 'degraded', 'dead', 'unknown'];

/**
 * Instances grid — the default landing page. Each card is one Chrome
 * process. The "Open Live" link on the card jumps to the VNC viewer +
 * action panel for that instance.
 */
export default function Instances() {
  const [filter, setFilter] = useState<InstanceStatus | 'all'>('all');

  const { data: instances = [], isLoading, error } = useQuery({
    queryKey: ['instances', { status: filter }],
    queryFn: () => listInstances(filter === 'all' ? {} : { status: filter as InstanceStatus }),
    refetchInterval: 5_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Instances</h1>
          <p className="text-sm text-slate-500">
            Every Chrome process the fleet currently knows about.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={cn(
                'text-xs px-3 py-1 rounded-full ring-1 transition',
                filter === s
                  ? 'bg-sky-500/15 text-sky-300 ring-sky-500/40'
                  : 'bg-slate-900/60 text-slate-400 ring-slate-700 hover:text-white',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          failed to load instances: {(error as Error).message}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500">loading…</div>
      ) : instances.length === 0 ? (
        <div className="text-sm text-slate-500">
          No instances yet. Wait ~15 s for the multi-chrome heartbeat agent
          to register, or check that <code className="font-mono">multi-chrome-heartbeat</code> is running.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {instances.map(i => <InstanceCard key={i.id} instance={i} />)}
        </div>
      )}
    </div>
  );
}
